/**
 * Jolpica-F1 client — free, unauthenticated Ergast-successor API.
 * -------------------------------------------------------------------------
 * https://github.com/jolpica/jolpica-f1
 *
 * URL strategy (see PROXY_BASE below): Jolpica is CORS-open
 * (access-control-allow-origin: *), so the dev proxy is a convenience, not a
 * requirement. In `npm run dev` we go through /api/jolpica/* (one place to add
 * auth/rate-limiting later); in a PACKAGED build (the Android APK bundle) there
 * is no dev server, so we call api.jolpi.ca directly. Without this, every
 * results/standings view is dead on the bundled phone app — the leaderboard's
 * only data path is this client.
 *
 * This is F1 Central's PRIMARY results data source.
 *   - Free, no API key, no request quota to babysit.
 *   - Full history back to 1950 (we expose 2000+ in the UI, see
 *     EARLIEST_SEASON).
 *   - Results, qualifying, sprint, standings, pit stops all come from the
 *     same backend with a consistent schema.
 *
 * OpenF1 is kept as a last-resort fallback (for network hiccups or years
 * where livetiming is unavailable) — see resultsService.js for the full
 * fallback chain.
 *
 * Rate limits (unauthenticated): 4 req/s burst, 500 req/hr sustained.
 * The in-memory cache below keeps normal browsing well under that.
 */

// import.meta.env.PROD is true ONLY in the `vite build` bundle (what the APK
// ships). Dev — including the phone loading the dev server over LAN via
// F1_DEV_SERVER_URL — stays on the proxy. Optional chaining keeps this safe
// under plain Node (the test scripts import this module; import.meta.env is
// undefined there → falls through to the proxy string, which they never call).
const PROXY_BASE = import.meta.env?.PROD
  ? 'https://api.jolpi.ca/ergast/f1'
  : '/api/jolpica';
const CACHE = new Map();
const TTL = 300_000;

/**
 * Jolpica's unauthenticated burst limit is 4 req/s, and the app can exceed it
 * easily: a standings view issues 3 calls near-simultaneously, and React
 * StrictMode double-mounts effects in dev. Measured 4 of 8 parallel requests
 * returning 429.
 *
 * Previously any 429 threw straight out of the primary tier, cascading into the
 * fallback chain for what is a purely transient condition. Retry with backoff
 * instead — mirroring the behaviour openF1Get already had.
 */
const RETRY_DELAYS_MS = [400, 1200];

async function get(path, { signal } = {}) {
  const cached = CACHE.get(path);
  if (cached && cached.expiresAt > Date.now()) return cached.value;

  let res;
  for (let attempt = 0; ; attempt++) {
    res = await fetch(`${PROXY_BASE}${path}`, { signal, headers: { Accept: 'application/json' } });
    const transient = res.status === 429 || res.status >= 500;
    if (!transient || attempt >= RETRY_DELAYS_MS.length) break;
    await new Promise((resolve, reject) => {
      const timer = setTimeout(resolve, RETRY_DELAYS_MS[attempt]);
      signal?.addEventListener('abort', () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      }, { once: true });
    });
  }

  if (!res.ok) {
    throw new Error(`Jolpica ${res.status} at ${path}`);
  }
  const json = await res.json();
  const value = json?.MRData ?? null;
  CACHE.set(path, { value, expiresAt: Date.now() + TTL });
  return value;
}

/**
 * The app exposes seasons from this year forward in the UI. Jolpica itself
 * has data back to 1950 — this floor is a product choice, not a data-source
 * limitation. Lower it freely if full history is ever wanted in the UI.
 */
export const EARLIEST_SEASON = 2000;

export function isSeasonAvailable(year) {
  return year >= 1950;
}

/* ===========================================================================
 * Race calendar + results
 * ========================================================================= */

/** fetchSeasonRaces — full calendar (schedule) for a season. */
export async function fetchSeasonRaces(season, { signal } = {}) {
  const data = await get(`/${season}/races/?limit=40`, { signal });
  return data?.RaceTable?.Races ?? [];
}

/** fetchRaceResult — full classification for one round. */
export async function fetchRaceResult(season, round, { signal } = {}) {
  const data = await get(`/${season}/${round}/results/?limit=40`, { signal });
  return data?.RaceTable?.Races?.[0] ?? null;
}

/**
 * fetchSeasonWinners — the P1 result of EVERY race in a season, in one
 * network call. Used by the "All Races" season overview instead of the
 * old per-race winner fetch loop.
 */
export async function fetchSeasonWinners(season, { signal } = {}) {
  const data = await get(`/${season}/results/1/?limit=40`, { signal });
  return data?.RaceTable?.Races ?? [];
}

/** fetchQualifying — combined Q1/Q2/Q3 classification for one round. */
export async function fetchQualifying(season, round, { signal } = {}) {
  const data = await get(`/${season}/${round}/qualifying/?limit=40`, { signal });
  return data?.RaceTable?.Races?.[0] ?? null;
}

/** fetchSprint — sprint race classification for one round (2021+). */
export async function fetchSprint(season, round, { signal } = {}) {
  const data = await get(`/${season}/${round}/sprint/?limit=40`, { signal });
  return data?.RaceTable?.Races?.[0] ?? null;
}

/** fetchPitStops — every pit stop in a round (2011+). Not wired into the UI yet. */
export async function fetchPitStops(season, round, { signal } = {}) {
  const data = await get(`/${season}/${round}/pitstops/?limit=100`, { signal });
  return data?.RaceTable?.Races?.[0]?.PitStops ?? [];
}

/* ===========================================================================
 * Standings
 * ========================================================================= */

/**
 * fetchDriverStandings — drivers' championship for a season (latest round).
 * Returns { round, standings } so callers can also access the round number.
 */
export async function fetchDriverStandings(season, { signal } = {}) {
  const data = await get(`/${season}/driverstandings/?limit=40`, { signal });
  const list = data?.StandingsTable?.StandingsLists?.[0];
  return { round: list?.round ? parseInt(list.round, 10) : null, standings: list?.DriverStandings ?? [] };
}

/**
 * fetchDriverStandingsAtRound — drivers' championship at a specific round.
 * Used for position-change delta (Tier 2 of the standings feature).
 */
export async function fetchDriverStandingsAtRound(season, round, { signal } = {}) {
  const data = await get(`/${season}/${round}/driverstandings/?limit=40`, { signal });
  const list = data?.StandingsTable?.StandingsLists?.[0];
  return list?.DriverStandings ?? [];
}

/**
 * fetchConstructorStandings — constructors' championship for a season (latest round).
 * Returns { round, standings }.
 */
export async function fetchConstructorStandings(season, { signal } = {}) {
  const data = await get(`/${season}/constructorstandings/?limit=40`, { signal });
  const list = data?.StandingsTable?.StandingsLists?.[0];
  return { round: list?.round ? parseInt(list.round, 10) : null, standings: list?.ConstructorStandings ?? [] };
}

/**
 * fetchConstructorStandingsAtRound — constructors' championship at a specific
 * round. Mirror of fetchDriverStandingsAtRound; both back the "standings after
 * round N" selector.
 */
export async function fetchConstructorStandingsAtRound(season, round, { signal } = {}) {
  const data = await get(`/${season}/${round}/constructorstandings/?limit=40`, { signal });
  const list = data?.StandingsTable?.StandingsLists?.[0];
  return list?.ConstructorStandings ?? [];
}

/* ===========================================================================
 * Season-long result histories
 *
 * One call returns every race a driver (or constructor) contested in a season,
 * which is what the expandable standings rows show. Doing it per-round instead
 * would be 24 requests against a 4 req/s burst limit.
 * ========================================================================= */

const RESULTS_PAGE_LIMIT = 100; // Jolpica's documented maximum

/**
 * Fetch a `/results/` collection, following pagination if the season is longer
 * than one page. A constructor's history is two rows per race, so a 24-race
 * season is 48 rows — comfortably inside one page today, but the guard costs
 * one comparison and stops this silently truncating if the calendar grows.
 */
async function fetchAllRaceResults(basePath, { signal }) {
  const first = await get(`${basePath}?limit=${RESULTS_PAGE_LIMIT}`, { signal });
  const races = first?.RaceTable?.Races ?? [];
  const total = parseInt(first?.total, 10);

  if (!Number.isFinite(total) || total <= RESULTS_PAGE_LIMIT) return races;

  const byRound = new Map(races.map((race) => [race.round, race]));
  for (let offset = RESULTS_PAGE_LIMIT; offset < total; offset += RESULTS_PAGE_LIMIT) {
    const page = await get(`${basePath}?limit=${RESULTS_PAGE_LIMIT}&offset=${offset}`, { signal });
    for (const race of page?.RaceTable?.Races ?? []) {
      // A page boundary can split one race's rows across two responses.
      const existing = byRound.get(race.round);
      if (existing) existing.Results = [...(existing.Results ?? []), ...(race.Results ?? [])];
      else byRound.set(race.round, race);
    }
  }

  return [...byRound.values()].sort((a, b) => parseInt(a.round, 10) - parseInt(b.round, 10));
}

/** fetchDriverSeasonResults — every race this driver finished (or didn't) in a season. */
export async function fetchDriverSeasonResults(season, driverId, { signal } = {}) {
  return fetchAllRaceResults(`/${season}/drivers/${driverId}/results/`, { signal });
}

/**
 * fetchConstructorSeasonResults — every race this constructor contested, with
 * BOTH cars' results attached to each race.
 */
export async function fetchConstructorSeasonResults(season, constructorId, { signal } = {}) {
  return fetchAllRaceResults(`/${season}/constructors/${constructorId}/results/`, { signal });
}

/* ===========================================================================
 * Season rosters — the entrants of a season, for name → id resolution
 *
 * The Oracle resolves a free-text "Hamilton" / "Ferrari" against these lists
 * before it can look up a head-to-head or a season summary. One call each,
 * cached like everything else.
 * ========================================================================= */

/** fetchSeasonDrivers — every driver entered in a season (id, names, code). */
export async function fetchSeasonDrivers(season, { signal } = {}) {
  const data = await get(`/${season}/drivers/?limit=100`, { signal });
  return data?.DriverTable?.Drivers ?? [];
}

/** fetchSeasonConstructors — every constructor entered in a season (id, name). */
export async function fetchSeasonConstructors(season, { signal } = {}) {
  const data = await get(`/${season}/constructors/?limit=100`, { signal });
  return data?.ConstructorTable?.Constructors ?? [];
}

/* ===========================================================================
 * Helpers — shape normalisation
 * ========================================================================= */

/**
 * parseStatus — normalises Jolpica's free-text `status` field into the
 * five buckets the UI already understands: Finished, Lapped, DNF, DNS, DSQ.
 *
 * Known formats seen across seasons:
 *   "Finished"                        → Finished
 *   "+1 Lap" / "+2 Laps"               → Lapped (pre-2025 text format)
 *   "Lapped"                          → Lapped (2025+ enum)
 *   "Disqualified"                    → DSQ
 *   "Did not start" / "Withdrew"       → DNS
 *   "Accident" / "Engine" / "Collision"
 *     / "Retired" / 100+ other reasons → DNF (bucketed — see
 *       docs/ergast_differences.md, all retirements collapse to "Retired"
 *       from the 2025 season onward on Jolpica's side too)
 */
export function parseStatus(statusField) {
  if (!statusField) return 'Finished';
  const s = statusField.trim();
  if (s === 'Finished') return 'Finished';
  if (s === 'Lapped' || /^\+\d+\s*laps?$/i.test(s)) return 'Lapped';
  if (s === 'Disqualified') return 'DSQ';
  if (s === 'Did not start' || s === 'Withdrew' || s === 'Did not qualify' || s === 'Did not prequalify') {
    return 'DNS';
  }
  return 'DNF';
}

/** driverDisplayName — "Max Verstappen" from a Jolpica Driver object. */
export function driverDisplayName(driver) {
  if (!driver) return 'Unknown';
  return `${driver.givenName ?? ''} ${driver.familyName ?? ''}`.trim() || 'Unknown';
}

/**
 * formatTimeGap — Jolpica's `Time.time` is the absolute race time for P1 and
 * the gap-to-leader for everyone else (e.g. "+5.598"). Drivers with no Time
 * object (DNF/DSQ) fall back to their status text.
 *
 * LAPPED DRIVERS ARE A TRAP. Classic Ergast never populated `Time` for a
 * lapped runner, so returning it verbatim was safe. Jolpica *does* populate it
 * (verified 2024–2026; 2015 and earlier have none), and for a lapped driver the
 * value is the interval to the car **ahead of them**, not the deficit to the
 * winner. Rendering it produced a non-monotonic gap column — e.g. 2026
 * Australian GP showed P6 "+54.617" followed by P7 "+4.593" for a driver a full
 * lap down. 16 of 19 races sampled were affected (114 driver rows).
 *
 * So: derive the lap deficit and never show `Time` for a lapped runner.
 *
 * @param {object} result      a Jolpica Results[] entry
 * @param {number} [winnerLaps] lap count of the classified winner; enables the
 *                              exact "+N Laps" deficit. Without it we can still
 *                              detect the 2025+ "Lapped" enum and degrade to a
 *                              plain "Lapped" rather than a misleading time.
 */
export function formatTimeGap(result, winnerLaps = null) {
  const status = (result.status ?? '').trim();

  // Pre-2025 seasons encode the deficit directly in the status text.
  if (/^\+\d+\s*laps?$/i.test(status)) return status;

  // Only *classified-but-lapped* runners get the deficit treatment. A lap count
  // below the winner's is NOT sufficient on its own — retirements and DNS
  // entries also fall short, and they must keep their status text.
  if (parseStatus(status) === 'Lapped') {
    const laps = result.laps != null ? parseInt(result.laps, 10) : NaN;
    const deficit = Number.isFinite(winnerLaps) && Number.isFinite(laps)
      ? winnerLaps - laps
      : 0;
    return deficit > 0 ? `+${deficit} Lap${deficit > 1 ? 's' : ''}` : 'Lapped';
  }

  if (result.Time?.time) return result.Time.time;
  return status || '-';
}

/** driverNumber — prefers the race-entry `number`, falls back to permanentNumber. */
export function driverNumber(result) {
  if (result.number) return parseInt(result.number, 10);
  if (result.Driver?.permanentNumber) return parseInt(result.Driver.permanentNumber, 10);
  return 0;
}

/**
 * gridPosition — "0" means the driver started from the pit lane (no fixed
 * grid slot, e.g. post-parc-fermé changes). Display as '-' like a missing
 * value rather than a misleading P0.
 */
export function gridPosition(gridField) {
  if (!gridField || gridField === '0') return '-';
  return parseInt(gridField, 10);
}

/** combineDateTime — Jolpica gives separate `date` + `time` (UTC, "Z"-suffixed). */
export function combineDateTime(date, time) {
  if (!date) return null;
  if (!time) return `${date}T12:00:00Z`;
  return `${date}T${time}`;
}
